import { useLingui } from '@lingui/react/macro'
import { createFileRoute } from '@tanstack/react-router'
import { useSignIn } from '@tshared/hooks/use-signin'
import { getErrorMessage, type HTTPError } from '@tshared/oapi/req'
import { App, Form, Input, Spin } from 'antd'
import { z } from 'zod'

import { LockOutlined, UserOutlined } from '@/icons'

import AuthFormLayout from '../-components/AuthFormLayout'
import { useFormValidation } from '../-hooks/useFormValidation'
import { useSignSuccess } from './-sign-success'

type SignInFormValues = {
  username: string
  password: string
}

export const Route = createFileRoute('/auth/sign-in')({
  component: SignInPage,
  validateSearch: z.object({
    code: z.string().nullish(),
    state: z.string().nullish(),
    redirect: z.string().nullish(),
  }),
})

function SignInPage() {
  const { t } = useLingui()
  const { message } = App.useApp()
  const { getRequiredRules } = useFormValidation()
  const { code, state, redirect: redirectTo } = Route.useSearch()
  const signSuccess = useSignSuccess()

  const loginMutation = useSignIn()

  const onFinish = async (values: SignInFormValues) => {
    loginMutation
      .mutateAsync({
        username: values.username,
        password: values.password,
      })
      .then((user) => {
        console.log(user, 'user')

        if (user) {
          void signSuccess(user, redirectTo)
        }
      })
      .catch(async (error: HTTPError) => {
        const errorData = error.data
        console.log(errorData)
        const errorMsg = getErrorMessage(error) || t`Sign in failed`
        message.error(errorMsg).then()
      })
  }

  return (
    <AuthFormLayout<SignInFormValues>
      title={t`Sign In`}
      subtitle={t`Welcome back`}
      submitText={t`Sign In`}
      loading={loginMutation.isPending}
      onFinish={onFinish}
    >
      <Form.Item
        name="username"
        rules={[
          ...getRequiredRules('username'),
          {
            max: 255,
            message: t`Account length cannot exceed 255 characters`,
          },
        ]}
      >
        <Input size="large" prefix={<UserOutlined />} placeholder={t`Enter account`} />
      </Form.Item>
      <Form.Item
        name="password"
        rules={[
          ...getRequiredRules('password'),
          {
            max: 128,
            message: t`Password length cannot exceed 128 characters`,
          },
        ]}
      >
        <Input.Password size="large" prefix={<LockOutlined />} placeholder={t`Enter password`} />
      </Form.Item>

      {code || state ? (
        <div
          className={'absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white'}
        >
          <Spin />
        </div>
      ) : null}
    </AuthFormLayout>
  )
}
